import Core from '../../Core';
import { TableProps } from '../types';
import { toast } from 'react-toastify';
import * as api from '../../../services/api';
import { useEffect, useState } from 'react';
import { DefaultMessage } from '../../../utils/defaultMessages';
import AdjustRoundedIcon from '@mui/icons-material/AdjustRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  List,
  Typography,
} from '@mui/material';
import SpreadsheetListItem from '../../../components/SpreadsheetListItem';
import DownloadXLSX from '../../../utils/DownloadXLSX/DownloadXLSX';
import Pagination from '../../Pricing/Pagination';

export default function Tables(): JSX.Element {
  const [productSelected, setProductSelected] = useState<string>('tractors');
  const [productFile, setProductFile] = useState<string>('trator');
  const [data, setData] = useState<TableProps[]>();
  const [productToDelete, setProductToDelete] = useState<string>();
  const [hashToDelete, setHashToDelete] = useState<string>();
  const [nameToDelete, setNameToDelete] = useState<string>();
  const [productSelectedDownload, setProductSelectedDownload] = useState<string>('tractor');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState(false);


  const getData = async (product: string, page: number = 1) => {
    setLoading(true)
    try {
      const response: any = await api.get(`table/retrieve/${product}/`, {
        status: 'all',
        page: page,
        page_size: 10,
      });
      setData(response.data.content.results);
      setTotalPages(response.data.content.pages)
    } catch {
      toast.error(DefaultMessage.ERROR, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoading(false)
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    getData(productSelectedDownload, newPage)
  };

  const deleteTable = async () => {
    toast.loading('Aguarde...', {
      position: toast.POSITION.TOP_RIGHT,
    });
    await api
      .destroy(`table/delete/${productToDelete}/${hashToDelete}/`)
      .then((response: any) => {
        toast.dismiss();
        if (response.status === 204) {
          getData(productToDelete!);
          setHashToDelete(undefined);
          setProductToDelete(undefined);
          setNameToDelete(undefined);

          toast.success('Planilha excluída com sucesso!', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 5000,
          });
        } else {
          toast.error(DefaultMessage.ERROR, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(DefaultMessage.ERROR, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const DialogComponent = (
    <Dialog
      open={hashToDelete !== undefined && productSelected !== undefined}
      onClose={() => {
        setHashToDelete(undefined);
        setProductToDelete(undefined);
        setNameToDelete(undefined);
      }}
      aria-describedby="alert-dialog-description"
    >
      <DialogContent>
        <Typography color="black">
          Tem certeza que deseja desativar a planilha {nameToDelete}?
        </Typography>
        <Typography color="red">
          Essa operação não poderá ser desfeita
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setHashToDelete(undefined);
            setProductToDelete(undefined);
            setNameToDelete(undefined);
          }}
          color="primary"
        >
          NÃO
        </Button>
        <Button
          onClick={() => {
            deleteTable();
            setHashToDelete(undefined);
            setProductToDelete(undefined);
            setNameToDelete(undefined);
          }}
        >
          SIM
        </Button>
      </DialogActions>
    </Dialog>
  );

  useEffect(() => {
    getData('1');
    setProductSelectedDownload('1')
    setProductFile('Trator')
  }, []);

  const onDownloadTable = async (hash: string, product: string, file: string) => {
    const fileName = `${productFile}_${file}`
    DownloadXLSX(`/table/download/xlsx/admin/${hash}/${product}/`, fileName)
  }

  return (
    <Core
      withNavbar
      withBlueBar
      path="/new-table"
      label="Clique aqui para voltar a cadastrar planilhas"
    >
      {DialogComponent}
      <Grid
        container
        direction="column"
        alignContent="center"
        alignItems="center"
        textAlign="center"
        paddingBottom={10}
      >
        <Chip label="Selecione um produto para filtrar a listagem" />
        <Grid
          container
          textAlign="center"
          justifyContent="center"
          paddingY={4}
          gap={2}
        >
          <Chip
            label="Tratores"
            color={productSelected === 'tractors' ? 'primary' : 'default'}
            variant={productSelected === 'tractors' ? 'outlined' : 'filled'}
            onClick={() => {
              setProductSelected('tractors');
              setProductSelectedDownload('1')
              setProductFile('Trator')
              getData('1');
            }}
            icon={
              productSelected === 'tractors' ? (
                <AdjustRoundedIcon />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )
            }
          />
          <Chip
            label="Helicópteros"
            color={productSelected === 'helicopters' ? 'primary' : 'default'}
            variant={productSelected === 'helicopters' ? 'outlined' : 'filled'}
            onClick={() => {
              setProductSelected('helicopters');
              setProductFile('Helicóptero')
              setProductSelectedDownload('2')
              getData('2');
            }}
            icon={
              productSelected === 'helicopters' ? (
                <AdjustRoundedIcon />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )
            }
          />
          <Chip
            label="Semirreboques"
            color={productSelected === 'trailers' ? 'primary' : 'default'}
            variant={productSelected === 'trailers' ? 'outlined' : 'filled'}
            onClick={() => {
              setProductSelected('trailers');
              setProductFile('Semirreboque')
              setProductSelectedDownload('3')
              getData('3');
            }}
            icon={
              productSelected === 'trailers' ? (
                <AdjustRoundedIcon />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )
            }
          />
          <Chip
            label="Carrocerias"
            color={productSelected === 'bodywork' ? 'primary' : 'default'}
            variant={productSelected === 'bodywork' ? 'outlined' : 'filled'}
            onClick={() => {
              setProductSelected('bodywork');
              setProductFile('Carroceria')
              setProductSelectedDownload('4')
              getData('4');
            }}
            icon={
              productSelected === 'bodywork' ? (
                <AdjustRoundedIcon />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )
            }
          />
          <Chip
            label="Colheitadeira"
            color={productSelected === 'Colheitadeira' ? 'primary' : 'default'}
            variant={
              productSelected === 'Colheitadeira' ? 'outlined' : 'filled'
            }
            onClick={() => {
              setProductSelected('Colheitadeira');
              setProductFile('Colheitadeira')
              setProductSelectedDownload('6')
              getData('6');
            }}
            icon={
              productSelected === 'Colheitadeira' ? (
                <AdjustRoundedIcon />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )
            }
          />
          <Chip
            label='Leves (0km)'
            color={productSelected === 'Leves (0km)' ? 'primary' : 'default'}
            variant={
              productSelected === 'Leves (0km)' ? 'outlined' : 'filled'
            }
            onClick={() => {
              setProductSelected('Leves (0km)');
              setProductFile('Leves (0km)')
              setProductSelectedDownload('7')
              getData('7');
            }}
            icon={
              productSelected === 'Leves (0km)' ? (
                <AdjustRoundedIcon />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )
            }
          />
        </Grid>
        {loading ? <Grid
          container
          direction="column"
          textAlign="center"
          alignContent="center"
          gap={2}
        >
          <CircularProgress size={100} />
        </Grid> :
          <List
            sx={{ width: '100%', maxWidth: 900, bgcolor: 'background.paper', }}
          >
            {data?.map((item, index) => (
              <div key={index}>
                <SpreadsheetListItem
                  item={item}
                  onDownload={(hash: string) => onDownloadTable(hash, productSelectedDownload, item.file_name)}
                />
                <Divider variant="inset" component="li" />

              </div>
            ))}
            <div className='mt-8'>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </List>

        }
      </Grid>
    </Core>
  );
}